import { LoadingButton, Timeline, TimelineConnector, TimelineContent, TimelineDot, TimelineItem, TimelineSeparator } from "@mui/lab";
import { Button, Chip, Dialog, InputAdornment, MenuItem, OutlinedInput, Skeleton, TextField } from "@mui/material";
import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import SvgIcon from "@mui/material/SvgIcon";
import Typography from "@mui/material/Typography";
import Grid from "@mui/material/Unstable_Grid2";
import { BarChartSquarePlus, Pencil01, Route } from "@untitled-ui/icons-react/build/esm";
import XIcon from "@untitled-ui/icons-react/build/esm/X";
import { FC, Fragment, useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { useBoolean } from "usehooks-ts";
import { CustomFieldRendererV2, CustomFieldsDialog } from "~/components/custom-fields-dialog";
import { ProductsTableSummary, TableItem, TableItemDisplay, TableSubtotalAddons } from "~/components/products-table-summary";
import { useAppRouter } from "~/hooks/use-app-router";
import { useTillSummary } from "~/hooks/use-till-summary";
import { CreateFileVariables, useCreateFile } from "~/queries/useCreateFile";
import { useGetAgents } from "~/queries/useGetAgents";
import { useGetClients } from "~/queries/useGetClients";
import { useGetCounters } from "~/queries/useGetCounters";
import { useGetCurrentCompanyCache } from "~/queries/useGetCurrentCompany";
import { useGetFileCustomFields } from "~/queries/useGetCustomFields";
import { useGetProcessesCache } from "~/queries/useGetProcesses";
import { useGetProductOptions } from "~/queries/useGetProductOptions";
import { useGetProducts } from "~/queries/useGetProducts";
import { useGetUsers } from "~/queries/useGetUsers";
import { useMeCache } from "~/queries/useMe";
import { useResetTill, useSetTillIsOpen, useTill, useUpdateTillCreateFileInput } from "~/store";
import { CounterConfigsInput, CUSTOM_FIELD_MODEL, CUSTOM_FIELD_TYPE, CustomFieldValueInput, MultiCurrencyConfigInput, MultiCurrencyCurrencyConfigInput, ROLE } from "~/types/__generated/gql/graphql";
import { TillFileInput } from "~/types/store";
import { DefineProcessPathsDialog } from "./define-process-paths-dialog";
import { paths } from "~/paths";
import { PostAdd, Comment } from "@mui/icons-material";
import { COUNTER_TYPE, DEFAULT_CURRENCY_CODE } from "~served/configs";
import { CustomPaperSection } from "~/components/custom-paper-section";
import { TextFieldInput } from "~/components/form-input/text-field-input";
import { $dayjs, DATETIMESTRING_FORMAT } from "~/libs/dayjs";
import { AutocompleteInput } from "~/components/form-input/autocomplete-input";
import { DateTimePickerInput } from "~/components/form-input/datetime-picker-input";
import { SelectInput } from "~/components/form-input/select-input";
export const CreateFileModal = () => {
  const {
    appQuery,
    currentActions: {
      isCreatingFile
    },
    router,
    goToFileDetailsOrderSheetQuery
  } = useAppRouter();
  const {
    data: company
  } = useGetCurrentCompanyCache();
  const {
    data: me
  } = useMeCache();
  const {
    data: processes = []
  } = useGetProcessesCache();
  const setTillIsOpen = useSetTillIsOpen();
  const till = useTill();
  const resetTill = useResetTill();
  const updateCreateFileInput = useUpdateTillCreateFileInput();
  const createFile = useCreateFile();
  const {
    data: agents = [],
    isLoading: isAgentsLoading
  } = useGetAgents();
  const {
    data: clients = [],
    isLoading: isClientsLoading
  } = useGetClients();
  const {
    createFileInputSummary,
    tillItemsSummary
  } = useTillSummary();
  const {
    data: users = [],
    isLoading: isUsersLoading
  } = useGetUsers();
  const {
    data: counters = [],
    isLoading: isCountersLoading
  } = useGetCounters();
  const {
    value: isProcessPathsDialogOpen,
    setTrue: openProcessPathsDialog,
    setFalse: closeProcessPathsDialog
  } = useBoolean(false);
  const {
    value: isCustomFieldsDialogOpen,
    setTrue: openCustomFieldsDialog,
    setFalse: closeCustomFieldsDialog
  } = useBoolean(false);
  const {
    data: customFields = []
  } = useGetFileCustomFields();
  const [customFieldValues, setCustomFieldValues] = useState<Record<string, CustomFieldValueInput>>({});
  // const [searchClient, setSearchClient] = useState<string>();
  const {
    data: products = []
  } = useGetProducts();
  const {
    data: productOptions = []
  } = useGetProductOptions();
  const onClose = async () => await router.push(paths.index);
  const handleCreateFileInColumn = async (columnId: string): Promise<void> => {
    if (columnId === "") toast.error(`No process found.`);
    const newFile = await createFile.mutateAsync({
      input: {
        ...till.createFileInput!,
        products: createFileInputSummary.products as CreateFileVariables["input"]["products"],
        cancelledProducts: createFileInputSummary.cancelledProducts as CreateFileVariables["input"]["cancelledProducts"],
        process: columnId,
        sortingIndex: processes.find(process => process._id === columnId)?._files?.length ?? 0,
        customFields: Object.values(customFieldValues)
      }
    });
    toast.success(`New ${company?.fileAlias?.singular} created`);
    void goToFileDetailsOrderSheetQuery(newFile._id);
  };
  useEffect(() => {
    if (isCreatingFile) {
      const fileCounter = counters.find(c => (c.type === COUNTER_TYPE.FILE && c.isDefault) === true)?._id ?? null;
      const invoiceCounter = counters.find(c => (c.type === COUNTER_TYPE.INVOICE && c.isDefault) === true)?._id ?? null;
      updateCreateFileInput({
        assignees: till.createFileInput?.assignees && till.createFileInput?.assignees.length > 0 ? till.createFileInput?.assignees : [],
        client: appQuery.clientId ?? undefined,
        agent: till.createFileInput?.agent ?? undefined,
        cancelledProducts: [],
        process: appQuery.processId,
        products: [],
        processPaths: [],
        isCancelled: false,
        isProcessed: false,
        customFields: [],
        counterConfigs: fileCounter || invoiceCounter ? {
          file: fileCounter ? {
            counter: fileCounter
          } as unknown as CounterConfigsInput : undefined,
          invoice: invoiceCounter ? {
            counter: invoiceCounter
          } as unknown as CounterConfigsInput : undefined
        } : null,
        ...(company?.isMultiCurrencyEnabled ? {
          multiCurrencyConfig: {
            selectedCurrency: company.currencyConfigs ? company.currencyConfigs.find(c => c.isPrimaryCurrency)?.code : DEFAULT_CURRENCY_CODE,
            currencyConfigs: company.currencyConfigs ? company.currencyConfigs.filter(c => c.isPrimaryCurrency || c.isSecondaryCurrency).map(c => ({
              code: c.code,
              rate: c.rate,
              isPrimaryCurrency: c.isPrimaryCurrency
            })) as unknown as MultiCurrencyCurrencyConfigInput : undefined
          } as unknown as MultiCurrencyConfigInput
        } : undefined),
        issueDate: new Date().toISOString(),
        dueDate: null
      });
    }
    return () => {
      if (isCreatingFile) {
        if (router.pathname !== "/clients/create") {
          resetTill();
          setCustomFieldValues({});
        }
      }
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCreatingFile, appQuery.processId, appQuery.clientId, isCountersLoading]);
  const productsTableItems = useMemo<TableItem[]>(() => createFileInputSummary.products.map(product => ({
    id: product._id.toString(),
    name: products.find(p => p._id === product.product)?.name ?? "[UNKNOWN PRODUCT]",
    quantity: product.quantity,
    subtotal: product.subtotal,
    options: product.options.map(option => ({
      id: option._id.toString(),
      name: productOptions.find(p => p._id === option.option)?.name ?? "[UNKNOWN PRODUCT OPTION]",
      quantity: option.quantity,
      subtotal: option.subtotal
    })),
    subtotalAddons: product.subtotalAddons as unknown as TableSubtotalAddons
  })), [createFileInputSummary.products, productOptions, products]);
  const fileItemsDisplay = useMemo<TableItemDisplay>(() => ({
    prev: tillItemsSummary.prev.products.map(product => ({
      id: product._id.toString(),
      name: products.find(p => p._id === product.product)?.name ?? "[UNKNOWN PRODUCT]",
      quantity: product.quantity,
      subtotal: product.subtotal,
      options: product.options.map(option => ({
        id: option._id.toString(),
        name: productOptions.find(p => p._id === option.option)?.name ?? "[UNKNOWN PRODUCT OPTION]",
        quantity: option.quantity,
        subtotal: option.subtotal
      })),
      subtotalAddons: product.subtotalAddons as unknown as TableSubtotalAddons
    })),
    add: tillItemsSummary.add.products.map(product => ({
      id: product._id.toString(),
      name: products.find(p => p._id === product.product)?.name ?? "[UNKNOWN PRODUCT]",
      quantity: product.quantity,
      subtotal: product.subtotal,
      options: product.options.map(option => ({
        id: option._id.toString(),
        name: productOptions.find(p => p._id === option.option)?.name ?? "[UNKNOWN PRODUCT OPTION]",
        quantity: option.quantity,
        subtotal: option.subtotal
      })),
      subtotalAddons: product.subtotalAddons as unknown as TableSubtotalAddons
    })),
    remove: tillItemsSummary.remove.products.map(product => ({
      id: product._id.toString(),
      name: products.find(p => p._id === product.product)?.name ?? "[UNKNOWN PRODUCT]",
      quantity: product.quantity,
      subtotal: product.subtotal,
      options: product.options.map(option => ({
        id: option._id.toString(),
        name: productOptions.find(p => p._id === option.option)?.name ?? "[UNKNOWN PRODUCT OPTION]",
        quantity: option.quantity,
        subtotal: option.subtotal
      })),
      subtotalAddons: product.subtotalAddons as unknown as TableSubtotalAddons
    }))
  }), [productOptions, products, tillItemsSummary.add.products, tillItemsSummary.prev.products, tillItemsSummary.remove.products]);
  const {
    value: updateFileCommentDialog,
    setTrue: openUpdateFileCommentDialog,
    setFalse: closeUpdateFileCommentDialog
  } = useBoolean(false);
  const UpdateOrderNumberComment: FC = () => {
    const [state, setState] = useState<string | null | undefined>();
    useEffect(() => {
      if (till.createFileInput?.fileSuffixComment) setState(till.createFileInput?.fileSuffixComment);
    }, []);
    return <Dialog open maxWidth="sm" data-sentry-element="Dialog" data-sentry-component="UpdateOrderNumberComment" data-sentry-source-file="create-file-modal.tsx">
				<Stack spacing={3} sx={{
        p: 3
      }} data-sentry-element="Stack" data-sentry-source-file="create-file-modal.tsx">
					<Typography variant="body2" data-sentry-element="Typography" data-sentry-source-file="create-file-modal.tsx">
						Update {company?.fileAlias.singular ?? "File"} Reference Number
						Comment
					</Typography>
					<Stack direction="row" spacing={3} justifyContent="flex-end" data-sentry-element="Stack" data-sentry-source-file="create-file-modal.tsx">
						<TextFieldInput id="file-number-comment" label="New option" value={state} onChange={e => setState(e.target.value)} data-sentry-element="TextFieldInput" data-sentry-source-file="create-file-modal.tsx" />
					</Stack>
					<Stack direction="row" spacing={3} justifyContent="flex-end" data-sentry-element="Stack" data-sentry-source-file="create-file-modal.tsx">
						<Button color="inherit" onClick={closeUpdateFileCommentDialog} data-sentry-element="Button" data-sentry-source-file="create-file-modal.tsx">
							Cancel
						</Button>
						<Button variant="contained" onClick={() => {
            const comment = state ?? "";
            updateCreateFileInput({
              fileSuffixComment: comment
            });
            void closeUpdateFileCommentDialog();
          }} data-sentry-element="Button" data-sentry-source-file="create-file-modal.tsx">
							Update
						</Button>
					</Stack>
				</Stack>
			</Dialog>;
  };
  const {
    value: updateInvoiceCommentDialog,
    setTrue: openUpdateInvoiceCommentDialog,
    setFalse: closeUpdateInvoiceCommentDialog
  } = useBoolean(false);
  const UpdateInvoiceNumberComment: FC = () => {
    const [state, setState] = useState<string | null | undefined>();
    useEffect(() => {
      if (till.createFileInput?.receiptSuffixComment) setState(till.createFileInput?.receiptSuffixComment);
    }, []);
    return <Dialog open maxWidth="sm" data-sentry-element="Dialog" data-sentry-component="UpdateInvoiceNumberComment" data-sentry-source-file="create-file-modal.tsx">
				<Stack spacing={3} sx={{
        p: 3
      }} data-sentry-element="Stack" data-sentry-source-file="create-file-modal.tsx">
					<Typography variant="body2" data-sentry-element="Typography" data-sentry-source-file="create-file-modal.tsx">
						Update Invoice Reference Number Comment
					</Typography>
					<Stack direction="row" spacing={3} justifyContent="flex-end" data-sentry-element="Stack" data-sentry-source-file="create-file-modal.tsx">
						<TextFieldInput id="file-number-comment" label="New option" value={state} onChange={e => setState(e.target.value)} data-sentry-element="TextFieldInput" data-sentry-source-file="create-file-modal.tsx" />
					</Stack>
					<Stack direction="row" spacing={3} justifyContent="flex-end" data-sentry-element="Stack" data-sentry-source-file="create-file-modal.tsx">
						<Button color="inherit" onClick={closeUpdateInvoiceCommentDialog} data-sentry-element="Button" data-sentry-source-file="create-file-modal.tsx">
							Cancel
						</Button>
						<Button variant="contained" onClick={() => {
            const comment = state ?? "";
            updateCreateFileInput({
              receiptSuffixComment: comment
            });
            void closeUpdateInvoiceCommentDialog();
          }} data-sentry-element="Button" data-sentry-source-file="create-file-modal.tsx">
							Update
						</Button>
					</Stack>
				</Stack>
			</Dialog>;
  };
  const OrderFormHeaderSection: FC = () => {
    return <Box sx={{
      p: 3,
      top: 0,
      zIndex: 10,
      width: "100%",
      display: "flex",
      position: "sticky",
      alignItems: "center",
      justifyContent: "space-between",
      backgroundColor: "background.paper"
    }} data-sentry-element="Box" data-sentry-component="OrderFormHeaderSection" data-sentry-source-file="create-file-modal.tsx">
				<Typography variant="h6" fontWeight={800} gutterBottom data-sentry-element="Typography" data-sentry-source-file="create-file-modal.tsx">
					New {company?.fileAlias.singular ?? "File"}
				</Typography>
				<Box display="flex" flexDirection="row" alignItems="center" data-sentry-element="Box" data-sentry-source-file="create-file-modal.tsx">
					<IconButton size="small" onClick={onClose} aria-label="Close" data-sentry-element="IconButton" data-sentry-source-file="create-file-modal.tsx">
						<SvgIcon data-sentry-element="SvgIcon" data-sentry-source-file="create-file-modal.tsx">
							<XIcon data-sentry-element="XIcon" data-sentry-source-file="create-file-modal.tsx" />
						</SvgIcon>
					</IconButton>
				</Box>
			</Box>;
  };
  const OrderFormGeneralInfoSection: FC = () => {
    return <CustomPaperSection key="order-form-general-info-section" title="General Information" data-sentry-element="CustomPaperSection" data-sentry-component="OrderFormGeneralInfoSection" data-sentry-source-file="create-file-modal.tsx">
				<Fragment key={`order-form-general-info-section-fragment-0`}>
					<Grid key="order-form-general-info-section-grid-0"
        // item
        xs={12} md={4} data-sentry-element="Grid" data-sentry-source-file="create-file-modal.tsx">
						<TextFieldInput id="om-created-by" label="Created By" value={me ? `${me.lastName} ${me.firstName}` : "Unknown User"} disabled data-sentry-element="TextFieldInput" data-sentry-source-file="create-file-modal.tsx" />
					</Grid>
					<Grid key="order-form-general-info-section-grid-1"
        // item
        xs={12} md={4} data-sentry-element="Grid" data-sentry-source-file="create-file-modal.tsx">
						<TextFieldInput id="om-created-at" label="Created At" value={$dayjs(new Date()).format(DATETIMESTRING_FORMAT)} disabled data-sentry-element="TextFieldInput" data-sentry-source-file="create-file-modal.tsx" />
					</Grid>
					<Grid key="order-form-general-info-section-grid-2"
        // item
        xs={12} md={4} data-sentry-element="Grid" data-sentry-source-file="create-file-modal.tsx">
						<TextFieldInput id="om-last-updated" label="Last Updated" value={$dayjs(new Date()).format(DATETIMESTRING_FORMAT)} disabled data-sentry-element="TextFieldInput" data-sentry-source-file="create-file-modal.tsx" />
					</Grid>
				</Fragment>
			</CustomPaperSection>;
  };
  const OrderFormBasicInfoSection: FC = () => {
    return <CustomPaperSection key="order-form-basic-info-section" title="Basic Information" data-sentry-element="CustomPaperSection" data-sentry-component="OrderFormBasicInfoSection" data-sentry-source-file="create-file-modal.tsx">
				<Fragment key={`order-form-basic-info-section-fragment-0`}>
					<Grid key="order-form-basic-info-section-grid-0"
        // item
        xs={12} md={4} data-sentry-element="Grid" data-sentry-source-file="create-file-modal.tsx">
						<AutocompleteInput id="om-select-client" label="Select Client" options={clients} renderOption={(props, option, {
            inputValue
          }) => {
            const label = `${option.companyIssuedId}-${option.name}`;
            const matches = label.toLowerCase().indexOf(inputValue.toLowerCase());
            const parts = matches !== -1 ? [label.substring(0, matches), label.substring(matches, matches + inputValue.length), label.substring(matches + inputValue.length)] : [label];
            return <li {...props} key={option._id} style={{
              whiteSpace: "pre-wrap"
            }}>
										{parts.map((part, index) => <Box key={index} component="span" sx={part.toLowerCase() === inputValue.toLowerCase() ? {
                fontWeight: 800,
                bgcolor: "primary.main"
              } : {}}>
												{part}
											</Box>)}
									</li>;
          }} value={clients.find(client => client._id === till.createFileInput?.client)} isOptionEqualToValue={(option, value) => option._id === (value?._id ?? undefined)} getOptionLabel={option => `${option.companyIssuedId}-${option.name}`} filterOptions={(options, {
            inputValue
          }) => {
            const input = inputValue.toLowerCase();
            const matchOptions = options.filter(o => {
              const label = `${o.companyIssuedId}-${o.name}`;
              const customFields = o.customFields.map(c => c.textValue).join(":");
              const contacts = (o.contacts ?? []).map(contact => `${contact.email}:${contact.firstName}:${contact.lastName}:${contact.mobile}:${contact.telephone}:${contact.customFields.filter(custom => custom._customField.type === CUSTOM_FIELD_TYPE.TEXT_SINGLE || custom._customField.type === CUSTOM_FIELD_TYPE.TEXT_MULTI).map(c => c.textValue).join(":")}`);
              const matchLabel = label.toLowerCase().includes(input);
              const matchSearch = `${customFields}:${contacts}`.toLowerCase().includes(input);
              return matchLabel || matchSearch;
            });
            const matches = matchOptions.filter(option => {
              const label = `${option.companyIssuedId}-${option.name}`;
              return label.toLowerCase().includes(input.toLowerCase());
            });
            const nonMatches = matchOptions.filter(option => {
              const label = `${option.companyIssuedId}-${option.name}`;
              return !label.toLowerCase().includes(input.toLowerCase());
            });
            return [...matches, ...nonMatches];
          }} onChange={(_, value) => updateCreateFileInput({
            client: value?._id
          })} onInput={e => {
            const event = e.nativeEvent as InputEvent;
            const input = e.target as any;
            if (event.inputType === "insertFromPaste") input.value = input.value.trim();
          }}
          // onInputChange={(_, value, reason) => {
          // 	if (reason === "input") setSearchClient(value);
          // }}
          renderInput={params => <TextField {...params} label="Clients" error={company?.isCreateFileWithoutClient ? false : !till.createFileInput?.client} helperText={company?.isCreateFileWithoutClient ? null : till.createFileInput?.client ? null : "Required"} />} data-sentry-element="AutocompleteInput" data-sentry-source-file="create-file-modal.tsx" />
					</Grid>
					<Grid key="order-form-basic-info-section-grid-1"
        // item
        xs={12} md={4} data-sentry-element="Grid" data-sentry-source-file="create-file-modal.tsx">
						<AutocompleteInput id="om-select-agents" label="Select Agents" options={agents} renderOption={(props, option, {
            inputValue
          }) => {
            const label = `${option.companyIssuedId}-${option.name}`;
            const matches = label.toLowerCase().indexOf(inputValue.toLowerCase());
            const parts = matches !== -1 ? [label.substring(0, matches), label.substring(matches, matches + inputValue.length), label.substring(matches + inputValue.length)] : [label];
            return <li {...props} key={option._id} style={{
              whiteSpace: "pre-wrap"
            }}>
										{parts.map((part, index) => <Box key={index} component="span" sx={part.toLowerCase() === inputValue.toLowerCase() ? {
                fontWeight: 800,
                bgcolor: "primary.main"
              } : {}}>
												{part}
											</Box>)}
									</li>;
          }} value={agents.find(agent => agent._id === till.createFileInput?.agent)} isOptionEqualToValue={(option, value) => option._id === value._id} getOptionLabel={option => `${option.companyIssuedId}-${option.name}`} filterOptions={(options, {
            inputValue
          }) => {
            const input = inputValue.toLowerCase();
            const matchOptions = options.filter(o => {
              const label = `${o.companyIssuedId}-${o.name}`;
              const customFields = o.customFields.map(c => c.textValue).join(":");
              const contacts = (o.contacts ?? []).map(contact => `${contact.email}:${contact.firstName}:${contact.lastName}:${contact.mobile}:${contact.telephone}:${contact.customFields.filter(custom => custom._customField.type === CUSTOM_FIELD_TYPE.TEXT_SINGLE || custom._customField.type === CUSTOM_FIELD_TYPE.TEXT_MULTI).map(c => c.textValue).join(":")}`);
              const matchLabel = label.toLowerCase().includes(input);
              const matchSearch = `${customFields}:${contacts}`.toLowerCase().includes(input);
              return matchLabel || matchSearch;
            });
            const matches = matchOptions.filter(option => {
              const label = `${option.companyIssuedId}-${option.name}`;
              return label.toLowerCase().includes(input.toLowerCase());
            });
            const nonMatches = matchOptions.filter(option => {
              const label = `${option.companyIssuedId}-${option.name}`;
              return !label.toLowerCase().includes(input.toLowerCase());
            });
            return [...matches, ...nonMatches];
          }} onChange={(_, value) => updateCreateFileInput({
            agent: value?._id
          })} onInput={e => {
            const event = e.nativeEvent as InputEvent;
            const input = e.target as any;
            if (event.inputType === "insertFromPaste") input.value = input.value.trim();
          }} renderInput={params => <TextField {...params} label="Agents" error={company?.isCreateFileWithoutAgent ? false : !till.createFileInput?.agent} helperText={company?.isCreateFileWithoutAgent ? null : till.createFileInput?.agent ? null : "Required"} />} data-sentry-element="AutocompleteInput" data-sentry-source-file="create-file-modal.tsx" />
					</Grid>
					<Grid key="order-form-basic-info-section-grid-2"
        // item
        xs={12} md={4} data-sentry-element="Grid" data-sentry-source-file="create-file-modal.tsx">
						<AutocompleteInput id="om-select-assignees" label="Select Assignees" multiple fullWidth disableCloseOnSelect options={users} value={users.filter(user => till.createFileInput?.assignees?.includes(user._id))} isOptionEqualToValue={(option, value) => option._id === value._id} getOptionLabel={option => option.firstName} onChange={(_, values) => {
            updateCreateFileInput({
              assignees: values.map(user => user._id)
            });
          }} renderInput={params => <TextField {...params} label="Users" />} renderTags={(value, getTagProps) => value.map((value, index) => <Chip {...getTagProps({
            index
          })} key={value._id} label={value.firstName} avatar={<Avatar src={value.avatar ?? undefined} />} />)} renderOption={(props, option) => <li {...props}>
									<Avatar src={option.avatar ?? undefined} />
									&nbsp;{option.firstName}
								</li>} data-sentry-element="AutocompleteInput" data-sentry-source-file="create-file-modal.tsx" />
					</Grid>
					<Grid key="order-form-basic-info-section-grid-3"
        // item
        xs={12} md={6} data-sentry-element="Grid" data-sentry-source-file="create-file-modal.tsx">
						<SelectInput id="om-select-order-counter-number" label={`${company?.fileAlias.singular ?? "File"} #`} value={till.createFileInput?.counterConfigs?.file ? till.createFileInput?.counterConfigs?.file.counter : null} onChange={e => {
            if (!e.target.value) return;
            updateCreateFileInput({
              counterConfigs: {
                ...till.createFileInput?.counterConfigs,
                file: {
                  counter: String(e.target.value)
                }
              }
            });
          }} renderValue={selected => {
            const sel = counters.find(c => c._id === selected);
            if (!sel) return "";
            return sel.isPredefinedFormat ? `${sel.prefix ? `${sel.prefix}-` : ""}YYYYMM${till.createFileInput?.fileSuffixComment ? `-${till.createFileInput?.fileSuffixComment}` : ""}` : `${sel.prefix}-${till.createFileInput?.fileSuffixComment ? `-${till.createFileInput?.fileSuffixComment}` : ""}`;
          }} input={<OutlinedInput endAdornment={<InputAdornment position="end">
											<IconButton onClick={e => {
              e.stopPropagation();
              void openUpdateFileCommentDialog();
            }} edge="end" size="small" aria-label="Update Comment" sx={{
              mr: 3
            }}>
												<Comment />
											</IconButton>
										</InputAdornment>} />} data-sentry-element="SelectInput" data-sentry-source-file="create-file-modal.tsx">
							{counters.filter(i => i.type === COUNTER_TYPE.FILE && i.isDisabled === false).map(counter => <MenuItem key={counter._id} value={counter._id}>
										{counter.isPredefinedFormat ? `${counter.prefix ? `${counter.prefix}-` : ""}YYYYMM` : counter.prefix}
									</MenuItem>)}
						</SelectInput>
					</Grid>
					<Grid key="order-form-basic-info-section-grid-4"
        // item
        xs={12} md={6} data-sentry-element="Grid" data-sentry-source-file="create-file-modal.tsx">
						<SelectInput id="om-select-invoice-counter-number" label={`Invoice #`} value={till.createFileInput?.counterConfigs?.file ? till.createFileInput?.counterConfigs?.file.counter : null} onChange={e => {
            if (!e.target.value) return;
            updateCreateFileInput({
              counterConfigs: {
                invoice: {
                  counter: String(e.target.value)
                }
              }
            });
          }} renderValue={selected => {
            const sel = counters.find(c => c._id === selected);
            if (!sel) return "";
            return sel.isPredefinedFormat ? `${sel.prefix ? `${sel.prefix}-` : ""}YYYYMM${till.createFileInput?.receiptSuffixComment ? `-${till.createFileInput?.receiptSuffixComment}` : ""}` : `${sel.prefix}-${till.createFileInput?.receiptSuffixComment ? `-${till.createFileInput?.receiptSuffixComment}` : ""}`;
          }} input={<OutlinedInput endAdornment={<InputAdornment position="end">
											<IconButton onClick={e => {
              e.stopPropagation();
              void openUpdateInvoiceCommentDialog();
            }} edge="end" size="small" aria-label="Update Comment" sx={{
              mr: 3
            }}>
												<Comment />
											</IconButton>
										</InputAdornment>} />} data-sentry-element="SelectInput" data-sentry-source-file="create-file-modal.tsx">
							{counters.filter(i => i.type === COUNTER_TYPE.FILE && i.isDisabled === false).map(counter => <MenuItem key={counter._id} value={counter._id}>
										{counter.isPredefinedFormat ? `${counter.prefix ? `${counter.prefix}-` : ""}YYYYMM` : counter.prefix}
									</MenuItem>)}
						</SelectInput>
					</Grid>
					<Grid key="order-form-basic-info-section-grid-5"
        // item
        xs={12} md={4} data-sentry-element="Grid" data-sentry-source-file="create-file-modal.tsx">
						<DateTimePickerInput id="" label="Issue Date" value={till.createFileInput?.issueDate ? new Date(till.createFileInput?.issueDate) : null} onChange={value => {
            if (!value) return;
            updateCreateFileInput({
              issueDate: value.toISOString()
            });
          }} data-sentry-element="DateTimePickerInput" data-sentry-source-file="create-file-modal.tsx" />
					</Grid>
					<Grid key="order-form-basic-info-section-grid-6"
        // item
        xs={12} md={4} data-sentry-element="Grid" data-sentry-source-file="create-file-modal.tsx">
						<DateTimePickerInput sx={{
            width: "100%"
          }} label="Due Date" value={till.createFileInput?.dueDate ? new Date(till.createFileInput?.dueDate) : null} onChange={value => {
            if (!value) return;
            updateCreateFileInput({
              dueDate: value.toISOString()
            });
          }} data-sentry-element="DateTimePickerInput" data-sentry-source-file="create-file-modal.tsx" />
					</Grid>
					<Grid key="order-form-basic-info-section-grid-7"
        // item
        xs={12} md={4} sx={{
          display: "flex",
          alignItems: "right",
          justifyContent: "right"
        }} data-sentry-element="Grid" data-sentry-source-file="create-file-modal.tsx">
						{!!till.createFileInput?.processPaths?.length && <Timeline sx={{
            m: 0,
            py: 0
          }}>
								{till.createFileInput.processPaths.slice(0, 3).map((path, index, current) => {
              const processData = processes.find(process => process._id === path.process);
              if (!processData) {
                return null;
              }
              return <TimelineItem key={index} sx={{
                "::before": {
                  content: "unset"
                }
              }}>
												<TimelineSeparator>
													<TimelineDot color={index === 0 ? "primary" : "grey"} />
													{index < 2 && !!current[index + 1] && <TimelineConnector />}
													{index === 2 && !!till.createFileInput!.processPaths[3] && <TimelineConnector />}
												</TimelineSeparator>
												<TimelineContent>{processData.name}</TimelineContent>
											</TimelineItem>;
            })}
							</Timeline>}
						<Button startIcon={<SvgIcon>
									<Route />
								</SvgIcon>} onClick={openProcessPathsDialog} data-sentry-element="Button" data-sentry-source-file="create-file-modal.tsx">
							Define process paths
						</Button>
					</Grid>
					{customFields.map(field => <Grid xs={12} sm={4} key={`${field._id}-input`}>
							<CustomFieldRendererV2 field={field} value={customFieldValues[field._id]} onChange={value => {
            const updatedCustomFieldValues = {
              ...customFieldValues,
              [value.customField]: value
            };
            setCustomFieldValues(updatedCustomFieldValues);
          }} />
						</Grid>)}
					{(me?.role === ROLE.SUPER_ADMIN || company?.isCustomFieldEnabled) && <Grid xs={12} textAlign="right">
							<Button startIcon={<BarChartSquarePlus />} onClick={openCustomFieldsDialog}>
								Manage custom fields
							</Button>
						</Grid>}
				</Fragment>
			</CustomPaperSection>;
  };
  const OrderProductInfoSection: FC = () => {
    return <CustomPaperSection key="order-product-info-section" title="Product Information" data-sentry-element="CustomPaperSection" data-sentry-component="OrderProductInfoSection" data-sentry-source-file="create-file-modal.tsx">
				<Fragment key={`order-product-info-section-fragment-0`}>
					<Grid xs={12} data-sentry-element="Grid" data-sentry-source-file="create-file-modal.tsx">
						{productsTableItems.length && <ProductsTableSummary items={productsTableItems} itemsDisplay={fileItemsDisplay} subtotal={createFileInputSummary.subtotal} additionalFee={createFileInputSummary.additionalFeeAmount} fixDiscount={createFileInputSummary.fixDiscountAmount} grandTotal={createFileInputSummary.grossAmount} />}
					</Grid>
				</Fragment>
			</CustomPaperSection>;
  };
  const isLoading = isAgentsLoading || isClientsLoading || isUsersLoading || isCountersLoading || !till.createFileInput;
  return <Dialog open fullWidth maxWidth={false} scroll="paper" data-sentry-element="Dialog" data-sentry-component="CreateFileModal" data-sentry-source-file="create-file-modal.tsx">
			{updateFileCommentDialog && <UpdateOrderNumberComment />}
			{updateInvoiceCommentDialog && <UpdateInvoiceNumberComment />}
			{isCustomFieldsDialogOpen && <CustomFieldsDialog model={CUSTOM_FIELD_MODEL.FILE} onClose={closeCustomFieldsDialog} />}

			<Stack gap={3} sx={{
      height: "100dvh",
      overflow: "auto"
    }} data-sentry-element="Stack" data-sentry-source-file="create-file-modal.tsx">
				{isLoading ? <>
						<Stack direction="row" justifyContent="space-between" spacing={1}>
							<Skeleton variant="rounded" height={40} sx={{
            flex: 1
          }} />
							<Skeleton variant="circular" height={40} width={40} />
						</Stack>
						<Box>
							<Skeleton variant="rounded" height={40} />
						</Box>
						<Box>
							<Grid container spacing={3}>
								<Grid xs={12} sm={4}>
									<Skeleton variant="rounded" height={40} width={120} />
								</Grid>
								<Grid xs={12} sm={8}>
									<Skeleton variant="rounded" height={40} sx={{
                flex: 1
              }} />
								</Grid>
								<Grid xs={12} sm={4}>
									<Skeleton variant="rounded" height={40} width={120} />
								</Grid>
								<Grid xs={12} sm={8}>
									<Skeleton variant="circular" height={40} width={40} />
								</Grid>
								<Grid xs={12} sm={4}>
									<Skeleton variant="rounded" height={40} width={120} />
								</Grid>
								<Grid xs={12} sm={8}>
									<Skeleton variant="rounded" height={40} sx={{
                flex: 1
              }} />
								</Grid>
								<Grid xs={12} sm={4}>
									<Skeleton variant="rounded" height={40} width={120} />
								</Grid>
								<Grid xs={12} sm={8}>
									<Skeleton variant="rounded" height={40} sx={{
                flex: 1
              }} />
								</Grid>
								<Grid xs={12} sm={4}>
									<Skeleton variant="rounded" height={40} width={120} />
								</Grid>
								<Grid xs={12} sm={8}>
									<Skeleton variant="rounded" height={40} sx={{
                flex: 1
              }} />
								</Grid>
								<Grid xs={12} sm={4}>
									<Skeleton variant="rounded" height={40} width={120} />
								</Grid>
								<Grid xs={12} sm={8}>
									<Skeleton variant="rounded" height={40} sx={{
                flex: 1
              }} />
								</Grid>
								<Grid xs={12}>
									<Skeleton variant="rounded" height={240} sx={{
                flex: 1
              }} />
								</Grid>
							</Grid>
						</Box>
					</> : <>
						{isProcessPathsDialogOpen && <DefineProcessPathsDialog initialValues={till.createFileInput?.processPaths ?? []} onDone={values => {
          updateCreateFileInput({
            processPaths: values as TillFileInput["processPaths"]
          });
          closeProcessPathsDialog();
        }} onCancel={closeProcessPathsDialog} />}
						<OrderFormHeaderSection />

						<Grid container spacing={3} px={2}>
							<Grid xs={12}>
								<OrderFormGeneralInfoSection />
							</Grid>
							<Grid xs={12}>
								<OrderFormBasicInfoSection />
							</Grid>
							<Grid xs={12}>
								<OrderProductInfoSection />
							</Grid>
						</Grid>

						<Box sx={{
          px: 2,
          py: 1,
          bottom: 0,
          zIndex: 10,
          width: "100%",
          display: "flex",
          position: "sticky",
          alignItems: "center",
          justifyContent: "end",
          backgroundColor: "background.paper"
        }}>
							<Grid container>
								<Grid xs={12}>
									<Box sx={{
                display: "flex",
                flexDirection: "row-reverse",
                my: 2
              }} gap={2}>
										<LoadingButton startIcon={<SvgIcon>
													<PostAdd />
												</SvgIcon>} loading={createFile.isLoading} variant="contained" onClick={() => handleCreateFileInColumn(appQuery.processId ?? "")}>
											Create
										</LoadingButton>
										<Button variant="contained" onClick={() => {
                  setTillIsOpen(true);
                }} startIcon={<SvgIcon>
													<Pencil01 />
												</SvgIcon>}>
											Add Products
										</Button>
									</Box>
								</Grid>
							</Grid>
						</Box>
					</>}
			</Stack>
		</Dialog>;
};